<template>
  <div class="team-members q-pa-sm q-px-md">
    <h2 class="team-members__title">Teams</h2>
    <p class="team-members__subtitle">Manage your team and their permissions</p>

    <!-- Loading state indicator -->
    <div v-if="isLoading" class="team-members__loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>

    <div v-else>
      <div class="team-members__list">
        <!-- Current user (always shown) -->
        <current-user-item :user="user" />

        <!-- Other team members -->
        <team-member-item v-for="member in teamMembers" :key="member.id" :member="member" @edit="handleEditUser" @remove="confirmRemoveMember" />

        <!-- Empty state if no other members -->
        <empty-state v-if="teamMembers.length === 0" />

        <!-- Add team member button -->
        <div class="text-center q-mt-md">
          <q-btn flat class="team-members__add-btn" size="sm" @click="handleInviteUser">
            <q-icon name="add" size="xs" class="q-mr-xs" />
            <span>Add team member</span>
          </q-btn>
        </div>
      </div>
    </div>

    <!-- Add/Edit Team Member Dialog -->
    <team-member-form-dialog v-model="showDialog" :editing-member="editingMember" :is-requesting="isRequesting" @save="saveMember" ref="memberForm" />

    <!-- Confirmation Dialog for removing members -->
    <confirmation-dialog v-model="showConfirmDialog" :is-requesting="isRequesting" @confirm="removeMember" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { UserApi } from '@api/index';
import CurrentUserItem from './TeamMembers/CurrentUserItem.vue';
import TeamMemberItem from './TeamMembers/TeamMemberItem.vue';
import EmptyState from './TeamMembers/EmptyState.vue';
import TeamMemberFormDialog from './TeamMembers/TeamMemberFormDialog.vue';
import ConfirmationDialog from './TeamMembers/ConfirmationDialog.vue';
import { checkTeamMemberLimit } from '@/utils/resourceMetrics';

export default {
  name: 'TeamMembers',

  components: {
    CurrentUserItem,
    TeamMemberItem,
    EmptyState,
    TeamMemberFormDialog,
    ConfirmationDialog
  },

  data() {
    return {
      isLoading: false,
      isRequesting: false,
      refreshTimeoutId: null,
      teamMembers: [],
      showDialog: false,
      showConfirmDialog: false,
      editingMember: null,
      memberToRemove: null,
      registration_guid: null,
      businessData: null
    };
  },

  computed: {
    ...mapGetters({
      user: 'user/getUser',
      maxMembers: 'resources/maxMembers',
      totalMembers: 'resources/totalMembers',
      memberCount: 'resources/memberCount'
    }),

    /**
     * Check if the user has reached their member limit
     * @returns {boolean} - True if the limit is reached
     */
    isMemberLimitReached() {
      // Only check for new members, not for editing existing ones
      if (this.editingMember) return false;

      // Use our utility function to check member limits
      const memberCheck = checkTeamMemberLimit();
      return !memberCheck.allowed;
    },

    /**
     * Get the maximum number of members allowed by the plan
     * @returns {string|number} - The maximum number of members or 'Unlimited'
     */
    maxMembersAllowed() {
      if (!this.user || !this.user.plan || !this.user.plan.plans) {
        return 'Unknown';
      }

      const maxMembers = this.user.plan.plans.max_members;
      return maxMembers === null ? 'Unlimited' : maxMembers;
    }
  },

  created() {
    this.fetchTeamMembers();
  },

  async mounted() {
    this.fetchTeamMembers();
    await this.fetchBusinessData();
  },

  methods: {
    async fetchBusinessData() {
      try {
        const data = await UserApi.getUser({ id: this.user.id, role: 'client' });
        if (data.statusCode === 200 && data.user) {
          this.registration_guid = data.user.registration_guid;

          if (this.registration_guid) {
            const { statusCode, data: businessData } = await UserApi.getBusinessByGuid(this.registration_guid);
            if (statusCode === 200 && businessData && businessData.user) {
              this.businessData = businessData.user;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    },

    /**
     * Get team member payload for API requests
     * @returns {Object} - API payload object
     */
    getTeamMemberPayload() {
      return {
        filters: {
          role: 'subClient',
          statuses: ['active', 'blocked', 'deleted', 'waiting']
        }
      };
    },

    /**
     * Normalize team member data to ensure consistent role format
     * @param {Array} members - Team members from API
     * @returns {Array} - Normalized team members
     */
    normalizeTeamMembers(members) {
      if (!Array.isArray(members)) return [];

      return members.map(member => ({
        ...member,
        // Ensure subClientRole is always lowercase for consistent comparison
        subClientRole: member.subClientRole ? String(member.subClientRole).toLowerCase() : 'viewer'
      }));
    },

    /**
     * Fetch and update team members
     * @param {boolean} force - Whether to force refresh (not used anymore but kept for compatibility)
     */
    async fetchTeamMembers(force = false) {
      if (!force) {
        this.isLoading = true;
      }

      try {
        const payload = this.getTeamMemberPayload();
        const response = await UserApi.searchUsers({ payload });
        const members = response.statusCode === 200 ? response.data.users : [];

        // Update with normalized data
        this.teamMembers = this.normalizeTeamMembers(members);
      } catch (error) {
        console.error('Error fetching team members:', error);
        if (!force) {
          this.$q.notify({
            type: 'negative',
            message: 'Failed to load team members. Please try again later.'
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    handleInviteUser() {
      this.editingMember = null;
      this.showDialog = true;
    },

    handleEditUser(member) {
      this.editingMember = member;
      this.showDialog = true;
    },

    confirmRemoveMember(member) {
      this.memberToRemove = member;
      this.showConfirmDialog = true;
    },

    /**
     * Extract and normalize role value from user data
     * @param {Object} userData - User data from form
     * @returns {string} - Normalized role value ('manager' or 'viewer')
     */
    extractRoleValue(userData) {
      let roleValue = 'viewer'; // Safe default

      if (typeof userData.role === 'string') {
        roleValue = userData.role.toLowerCase();
      } else if (userData.role && userData.role.value) {
        roleValue = userData.role.value.toLowerCase();
      }

      // Final validation to ensure it's exactly 'manager' or 'viewer'
      return roleValue === 'manager' ? 'manager' : 'viewer';
    },

    /**
     * Prepare payload for updating an existing team member
     * @param {Object} userData - User data from form
     * @returns {Object} - API payload
     */
    prepareUpdatePayload(userData) {
      const fullName = `${userData.firstName} ${userData.lastName}`.trim();
      const formattedPhone = userData.phoneNumber.replace(/\s/g, '');

      return {
        ...this.editingMember,
        role: 'client',
        name: fullName,
        contact_name: fullName,
        phone: formattedPhone,
        contact_phone: formattedPhone
      };
    },

    /**
     * Prepare payload for creating a new team member
     * @param {Object} userData - User data from form
     * @returns {Object} - API payload
     */
    prepareCreatePayload(userData) {
      const formattedPhone = userData.phoneNumber.replace(/\s/g, '');

      return {
        additionalInformations: {
          company_name: this.businessData?.company_name || 'Default Company',
          industry: this.businessData?.industry || 'Technology',
          contact_name: userData.firstName,
          contact_surname: userData.lastName,
          contact_email: userData.email,
          contact_phone: formattedPhone,
          main_location: this.businessData?.main_location || '',
          country: { value: this.businessData?.country || 'US' }
        },
        password: this.generatePassword(12),
        user: {
          name: userData.firstName,
          surname: userData.lastName,
          email: userData.email,
          phone: formattedPhone
        },
        paymentDetail: {
          type: 'test',
          details: {}
        }
      };
    },

    /**
     * Refresh team members data with direct API call
     * @param {boolean} showRefreshIndicator - Whether to show the refresh overlay
     */
    async refreshTeamMembersDirectly(showRefreshIndicator = true) {
      // Clear any pending refresh timeouts
      if (this.refreshTimeoutId) {
        clearTimeout(this.refreshTimeoutId);
        this.refreshTimeoutId = null;
      }

      if (showRefreshIndicator) {
        this.isLoading = true;
      }

      try {
        const payload = this.getTeamMemberPayload();
        const response = await UserApi.searchUsers({ payload });

        if (response.statusCode === 200 && Array.isArray(response.data.users)) {
          this.teamMembers = this.normalizeTeamMembers(response.data.users);
        }
      } catch (error) {
        console.error('Error in direct refresh:', error);
        this.fetchTeamMembers(true); // Fall back to standard fetch
      } finally {
        if (showRefreshIndicator) {
          // Keep the loading indicator visible for at least 500ms for better UX
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        }
      }
    },

    /**
     * Save or update a team member
     * @param {Object} userData - Form data for the team member
     */
    async saveMember(userData) {
      if (!this.editingMember) {
        // Use our utility to check member limits
        const memberCheck = checkTeamMemberLimit();

        if (!memberCheck.allowed) {
          this.$q.notify({
            type: 'negative',
            position: 'top',
            message: `Unable to add team member: You have reached the maximum limit of ${memberCheck.maxMembers} members for your current plan.`,
            timeout: 8000
          });
          return;
        }
      }

      this.isRequesting = true;

      try {
        const roleValue = this.extractRoleValue(userData);

        if (this.editingMember) {
          // Update existing team member
          const payload = this.prepareUpdatePayload(userData);
          const response = await UserApi.updateUser({
            id: this.editingMember.id,
            payload: payload
          });

          // Update role if it changed
          if (response.statusCode === 200 && roleValue !== this.editingMember.subClientRole) {
            await UserApi.updateSubClientRole({
              id: this.editingMember.id,
              payload: { role: roleValue }
            });
          }
        } else {
          const payload = this.prepareCreatePayload(userData);
          await UserApi.createBusiness(payload, this.registration_guid, false, roleValue);
        }

        // Show success notification
        this.$q.notify({
          type: 'positive',
          message: `Team member ${this.editingMember ? 'updated' : 'added'} successfully`
        });

        // Close dialog first for responsive feeling
        this.showDialog = false;

        if (!this.editingMember) {
          await this.refreshUserData(this.teamMembers.length + 1);
        } else {
          // For edits, count stays the same
          const editedMemberId = this.editingMember.id;
          this.teamMembers = this.teamMembers.map(member => {
            if (member.id === editedMemberId) {
              return {
                ...member,
                first_name: userData.firstName,
                last_name: userData.lastName,
                contact_phone: userData.phoneNumber,
                subClientRole: this.extractRoleValue(userData)
              };
            }
            return member;
          });
        }
        if (!this.editingMember) {
          // For new members, increment the current count by 1
          await this.refreshUserData(this.teamMembers.length + 1);
        } else {
          // For edits, count stays the same
          await this.refreshUserData(this.teamMembers.length);
        }

        // Schedule a refresh after a delay to ensure server processing is complete
        this.refreshTimeoutId = setTimeout(() => {
          this.refreshTeamMembersDirectly(true);
        }, 800);
      } catch (error) {
        console.error('Error saving team member:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to save team member. Please try again later.'
        });
      } finally {
        this.isRequesting = false;
      }
    },

    /**
     * Remove a team member
     * Handles the API call and UI updates after successful removal
     */
    async removeMember() {
      if (!this.memberToRemove) return;

      this.isRequesting = true;
      try {
        const response = await UserApi.deleteSubClient(this.memberToRemove.id);

        if (response.statusCode === 200) {
          // Show success notification
          this.$q.notify({
            type: 'positive',
            message: 'Team member removed successfully'
          });

          // Close dialog and reset state
          this.showConfirmDialog = false;
          const removedMemberId = this.memberToRemove.id;
          this.memberToRemove = null;

          // Optimistic UI update - remove the member immediately from local state
          this.teamMembers = this.teamMembers.filter(member => member.id !== removedMemberId);

          // Refresh user data to update team member counts in UserDropdown
          await this.refreshUserData();

          // Schedule a background refresh to ensure consistency with server
          this.refreshTimeoutId = setTimeout(() => {
            // Use silent refresh (no indicator) since we already updated the UI
            this.refreshTeamMembersDirectly(false);
          }, 500);
        }
      } catch (error) {
        console.error('Error removing team member:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to remove team member. Please try again later.'
        });
      } finally {
        this.isRequesting = false;
      }
    },
    async refreshUserData(memberCount = null) {
      try {
        // Create a deep copy of the current user to ensure proper reactivity
        const currentUser = JSON.parse(JSON.stringify(this.user));

        // Use provided count or fall back to current array length
        const actualMemberCount = memberCount !== null ? memberCount : this.teamMembers.length;

        // Update the total_members count in the user's plan
        if (currentUser.plan) {
          currentUser.plan.total_members = actualMemberCount;
        }
        this.$store.dispatch('user/saveUser', currentUser);
      } catch (error) {
        console.error('Error updating user data in store:', error);
      }
    },

    generatePassword(length) {
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_-+=<>?';
      let password = '';
      const passwordChecker = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})');

      do {
        password = '';
        for (let i = 0; i < length; i++) {
          const randomIndex = Math.floor(Math.random() * charset.length);
          password += charset[randomIndex];
        }
      } while (!passwordChecker.test(password));

      return password;
    }
  }
};
</script>

<style lang="scss" scoped>
.team-members {
  width: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: left;
  overflow: hidden;
  position: relative; /* For positioning the overlay */
  padding: 16px 24px;

  &__title {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0 0 2px 0;
  }

  &__subtitle {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0 0 24px 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
  }

  &__list {
    background-color: var(--secondary-bg-color);
    border-radius: 8px;
  }

  &__add-btn {
    margin: 8px auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--main-text-color);
    background-color: var(--terziary-btn-color);
    padding: 6px 12px;
    transition: background-color 0.3s;
    border-radius: 8px;
    @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color));
  }
}
</style>
